/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader } from '../loader'
import { useModuleAccess } from '../../hooks/useModuleAccess'
import {
  NO_ACCESS_PATH,
  moduleForPath,
  resolveLandingPath,
} from '../../constants/appModules'

/**
 * Stops a user opening a module they were not granted by typing or pasting its address.
 *
 * The module is derived from the current path rather than declared per route, so every screen under
 * a gated prefix is covered the day it is added and there is no second list to keep in step with
 * `ROUTE_MODULE_RULES`. A path no rule claims is let through: that is shared plumbing or an auth
 * flow, and matches the fail-open rule the server applies to unmapped endpoints.
 *
 * Renders the loader — never a redirect — until `/me` resolves, so a slow response cannot flash the
 * no-access screen at somebody who does have access.
 */
export const ModuleRoute = ({ children }) => {
  const location = useLocation()
  const { isReady, isUnrestricted, moduleSet, hasModule } = useModuleAccess()

  const requiredModule = moduleForPath(location.pathname)

  if (!requiredModule || isUnrestricted) return <React.Fragment>{children}</React.Fragment>

  if (!isReady) {
    return (
      <div data-testid="module-route-loading">
        <Loader />
      </div>
    )
  }

  if (hasModule(requiredModule)) return <React.Fragment>{children}</React.Fragment>

  // Sends a denied user on to the first module they can actually open rather than the no-access
  // screen — originally just for the dashboard (the app's own choice of destination after login),
  // now for any blocked URL: a user who holds nine modules and mistypes the tenth's address should
  // land back in the app they can use, not on a dead end with no sidebar and no way back. Verified
  // against `hasModule` before using it so a landing candidate the grant does not actually cover
  // (only reachable if a future module/landing-path pairing goes stale) can never send `Navigate`
  // straight back to a route this same check would immediately reject again.
  const landingPath = resolveLandingPath(Array.from(moduleSet))
  const landingModule = moduleForPath(landingPath)
  const fallback = landingModule && !hasModule(landingModule) ? NO_ACCESS_PATH : landingPath

  return <Navigate to={fallback} replace />
}

export default ModuleRoute
